import { Component, Input, ViewChild, OnDestroy, ElementRef, forwardRef } from "@angular/core";
import { LogService } from "ng/src/app/logs/log.service";
import { FileAttachment } from "ng/src/app/notes/shared/file-attachments/file-attachment.model";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { FileService } from "ng/src/app/files/file.service";
import { FileUpload } from "primeng/fileupload";
import { finalize, takeUntil} from "rxjs/operators";
import { Subject } from "rxjs";

@Component({
	selector: 'lb-file-uploader',
	templateUrl: './file-uploader.component.html',
	styleUrls: ['./file-uploader.component.scss'],
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		multi: true,
		useExisting: forwardRef(() => FileUploaderComponent)
	}]
})
export class FileUploaderComponent implements ControlValueAccessor, OnDestroy {
	@ViewChild('uploader') uploader!: FileUpload;
	
	@Input() logId = LogService.selectedLog!.id;
	@Input('disabled') _disabled = false;
	@Input() fileLimit?: number;
	
	private _files: FileAttachment[] = [];

	get hasFiles() {
		return this.files.some(fa => fa.description != 'Rich Text Image');
	}
	
	get files() {
		return this._files;
	}
	set files(val) {
		this._files = (Array.isArray(val)) ? val : [];
	}
	
	onChange = (files: FileAttachment[]) => {};
	onTouched = () => {};
	touched = false;
	
	get disabled() {
		if (this.uploader?.uploading || this._disabled)
			return true;
		
		const atFileLimit = !!this.fileLimit && (this.files.length + this.totalPendingFiles) >= this.fileLimit;
		
		return atFileLimit;
	}
	
	get totalPendingFiles() {
		return this.uploader?.files.length ?? 0;
	}
	
	maxFileSize = FileService.MAX_FILE_SIZE;
	
	currentUploads: {
		file: File,
		canceller: Subject<any>,
		completed: boolean,
		cancel: () => void
	}[] = [];
	
	private destroy$ = new Subject();
	
	focus() {
		((this.elRef.nativeElement as HTMLElement).getElementsByClassName('p-fileupload-choose')
			.item(0) as HTMLElement)?.focus()
	}
	
	constructor(
		private fileService: FileService,
		private elRef: ElementRef
	) {}
	
	ngOnDestroy(): void {
		this.destroy$.next();
	}
	
	registerOnChange(fn: (files: FileAttachment[]) => void): void {
		this.onChange = fn;
	}
	
	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}
	
	setDisabledState(isDisabled: boolean): void {
		this._disabled = isDisabled;
	}
	
	writeValue(obj: FileAttachment[]): void {
		this.files = obj;
	}
	
	handleUpload(event: { files: File[] }) {
		this.uploader.progress = 0;
		this.uploader.uploading = true;

		this.currentUploads = event.files.map((file) => {
			const canceller = new Subject();

			return {
				file: file,
				canceller: canceller,
				completed: false,
				cancel: () => {
					canceller.next(null);
				}
			}
		});
		
		const hasRemainingUploads = () => this.currentUploads.some(u => !u.completed);
		
		this.fileService.uploadFiles(event.files, this.logId)
			.forEach((ob$, idx) => {
				const controller = this.currentUploads[idx];

				ob$.pipe(
					takeUntil(controller.canceller),
					finalize(() => {
						controller.completed = true;
						
						if (!hasRemainingUploads())
						{
							this.uploader.uploading = false;
							this.uploader.progress = 100;
							this.uploader?.clear();
							this.currentUploads = [];
						}
						else {
							const idx = this.uploader.files.indexOf(controller.file);
							if (idx > -1)
								this.uploader.files.splice(idx, 1);
							
							const completed = this.currentUploads.reduce((current, status) => {
								return status.completed ? current + 1 : current;
							}, 0);

							this.uploader.progress = (completed / this.currentUploads.length) * 100;
						}
						
						this.uploader.cd.detectChanges();
					})
				).subscribe(file => {
					controller.completed = true;
					
					file && this.addFile(file);
				})
			});
	}
	
	removeFile(fa: FileAttachment) {
		const files = this.files,
			  idx = files.indexOf(fa);

		if (idx === -1)
			return;
		
		files.splice(idx, 1);

		this.onChange(files);
	}
	
	cancelUpload(file: File) {
		this.currentUploads.find(x => x.file === file)?.cancel();
	}
	
	onDrop(event: DragEvent) {
		if (this.disabled)
			return;
		
		const files: File[] = [];

		for (let i = 0; i < (event.dataTransfer?.files.length ?? 0); i++) {
			const file = event.dataTransfer?.files[i];
			
			file && files.push(file);
		}
		
		if (!files.length)
			return;
		
		this.uploader?.files.push(...files);
		this.uploader?.upload();
	}
	
	private addFile(...fa: FileAttachment[]) {
		this.files = [...this.files, ...fa];

		this.onChange(this.files);
	}
}